import React, { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Alert,
  Row,
  Col,
  Typography,
  Space,
} from "antd";
import {
  LockOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import AuthService from "../../services/auth.service";

const { Title, Text } = Typography;

const ChangePassword = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error("Les nouveaux mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    setSuccess(false);

    try {
      await AuthService.changePassword(values.oldPassword, values.newPassword);
      message.success("Mot de passe modifié avec succès");
      setSuccess(true);
      form.resetFields();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Erreur lors du changement de mot de passe";
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject(new Error("Veuillez saisir un mot de passe"));
    }
    if (value.length < 6) {
      return Promise.reject(
        new Error("Le mot de passe doit contenir au moins 6 caractères")
      );
    }
    if (!/(?=.*[a-z])/.test(value)) {
      return Promise.reject(
        new Error("Le mot de passe doit contenir au moins une minuscule")
      );
    }
    if (!/(?=.*[A-Z])/.test(value)) {
      return Promise.reject(
        new Error("Le mot de passe doit contenir au moins une majuscule")
      );
    }
    if (!/(?=.*\d)/.test(value)) {
      return Promise.reject(
        new Error("Le mot de passe doit contenir au moins un chiffre")
      );
    }
    return Promise.resolve();
  };

  return (
    <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          borderRadius: "12px",
        }}
      >
        <div style={{ marginBottom: "32px", textAlign: "center" }}>
          <SafetyOutlined
            style={{
              fontSize: "48px",
              color: "#667eea",
              marginBottom: "16px",
            }}
          />
          <Title level={2} style={{ marginBottom: "8px" }}>
            Changer le mot de passe
          </Title>
          <Text type="secondary">
            Assurez-vous d'utiliser un mot de passe fort et sécurisé
          </Text>
        </div>

        {success && (
          <Alert
            message="Succès"
            description="Votre mot de passe a été modifié avec succès"
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: "24px" }}
            closable
            onClose={() => setSuccess(false)}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label="Ancien mot de passe"
            name="oldPassword"
            rules={[
              {
                required: true,
                message: "Veuillez saisir votre ancien mot de passe",
              },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#667eea" }} />}
              placeholder="Saisissez votre ancien mot de passe"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Nouveau mot de passe"
            name="newPassword"
            rules={[
              {
                required: true,
                message: "Veuillez saisir un nouveau mot de passe",
              },
              { validator: validatePassword },
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#667eea" }} />}
              placeholder="Saisissez votre nouveau mot de passe"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Confirmer le nouveau mot de passe"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              {
                required: true,
                message: "Veuillez confirmer votre nouveau mot de passe",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Les deux mots de passe ne correspondent pas")
                  );
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#667eea" }} />}
              placeholder="Confirmez votre nouveau mot de passe"
              size="large"
            />
          </Form.Item>

          <div
            style={{
              background: "#f0f5ff",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
            }}
          >
            <Text strong style={{ display: "block", marginBottom: "8px" }}>
              Règles du mot de passe :
            </Text>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              <li>
                <Text type="secondary">Au moins 6 caractères</Text>
              </li>
              <li>
                <Text type="secondary">
                  Au moins une lettre minuscule (a-z)
                </Text>
              </li>
              <li>
                <Text type="secondary">
                  Au moins une lettre majuscule (A-Z)
                </Text>
              </li>
              <li>
                <Text type="secondary">Au moins un chiffre (0-9)</Text>
              </li>
            </ul>
          </div>

          <Form.Item>
            <Space style={{ width: "100%", justifyContent: "center" }}>
              <Button
                type="default"
                size="large"
                onClick={() => form.resetFields()}
                style={{ minWidth: "120px" }}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                style={{
                  minWidth: "120px",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                }}
              >
                Modifier
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ChangePassword;